<template>
  <div v-if="dataIsLoad">
    <ul class="addon-list">
      <li
        v-for="(item, index) in addonData"
        :key="item.CustomMarketID_withoutSize"
        class="addon-list-item"
      >
        <div v-if="showOutsideInfo" class="addon-img-block">
          <div class="addon-img" @click.prevent="getItemDetail(item.CustomMarketID_withoutSize)">
            <NewImage :url="item.Cover" :size="imageSize" :name="item.Name" />
          </div>
          <a class="popup-text" @click.prevent="getItemDetail(item.CustomMarketID_withoutSize)">
            尺寸表 / 資訊
          </a>
        </div>
        <a v-else class="addon-img" @click.prevent="getItemDetail(item.CustomMarketID_withoutSize)">
          <NewImage :url="item.Cover" :size="imageSize" :name="item.Name" />
        </a>
        <div class="addon-info">
          <a class="item-name" @click.prevent="getItemDetail(item.CustomMarketID_withoutSize)">
            {{ item.Name }}
          </a>
          <div class="item-price">
            <span v-if="item.OriginPrice !== item.SellPrice" class="origin-price">
              NT.{{ item.OriginPrice }}
            </span>
            <span class="sell-price">NT.{{ item.SellPrice }}</span>
          </div>
          <div class="addon-shop-select select-style">
            <select v-if="!hasMainItem" class="spec-select" @click.prevent="checkAddonInCart">
              <option value="default">規格</option>
            </select>
            <select
              v-else
              v-model="selectedCustomMarketID[index]"
              class="spec-select"
              @click.prevent="openSelector"
            >
              <option value="default">規格</option>
              <option
                v-for="spec in item.Specs"
                :key="spec.CustomMarketID"
                :disabled="!spec.HasStock"
                :value="spec.CustomMarketID"
              >
                {{ spec.ColorName }}-{{ spec.Size }} {{ !spec.HasStock ? '(已售完)' : '' }}
              </option>
            </select>
            <div v-if="!hasMainItem" class="UiQuantity count-select">
              <a class="UiQuantity__btn UiQuantity__btn--minus" @click.prevent="checkAddonInCart">
                -
              </a>
              <span class="UiQuantity__text">1</span>
              <a class="UiQuantity__btn UiQuantity__btn--plus" @click.prevent="checkAddonInCart">
                +
              </a>
            </div>
            <div v-else class="UiQuantity count-select">
              <a class="UiQuantity__btn UiQuantity__btn--minus" @click.prevent="onMinus(index)">
                -
              </a>
              <span class="UiQuantity__text">{{ selectedCount[index] }}</span>
              <a class="UiQuantity__btn UiQuantity__btn--plus" @click.prevent="onPlus(index)">+</a>
            </div>
          </div>
          <button
            v-if="!hasMainItem"
            class="btn btn-add-cart addon-add"
            @click.prevent="checkAddonInCart"
          >
            加入購物車
          </button>
          <button v-else class="btn btn-add-cart addon-add" @click.prevent="addToCart(index)">
            加入購物車
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

import { fireAddToCart } from '@global/gtm/common';

import useMiniCartStore from '@global/stores/useMiniCartStore';

import useUtmKDetect from '@global/composable/useUtmKDetect';
import useMsg from '@global/composable/useMessage';
import useAlert from '@global/composable/useAlert';
import useAxiosAPI from '@global/composable/api/useAxiosAPI';

import NewImage from '@global/components/common/Image/NewImage.vue';

const { postShoppingCartAPI, getDataAPI } = useAxiosAPI();
const { utmKDetect, shareCode } = useUtmKDetect();
const { messageBox } = useMsg();
const { alertDialog } = useAlert();

const props = defineProps({
  addonData: {
    type: Array,
    required: true,
  },
  dataIsLoad: {
    type: Boolean,
    required: true,
  },
  hasMainItem: {
    type: Boolean,
    required: true,
  },
  imageSize: {
    type: String,
    required: true,
  },
  showOutsideInfo: {
    type: Boolean,
    required: true,
  },
});

const selectedCustomMarketID = ref(props.addonData.map(() => 'default'));
const selectedCount = ref(props.addonData.map(() => 1));
const selectIsOpen = ref(false);

const stockArray = computed(() => {
  const stockArr = [];
  props.addonData.forEach((item) => {
    const specStockCount = [];

    item.Specs.forEach((spec) => {
      specStockCount.push({
        customMarketID: spec.CustomMarketID,
        stock: spec.Stock,
      });
    });
    stockArr.push(specStockCount);
  });
  return stockArr;
});

const emit = defineEmits(['showPopupDetail', 'showAddonWarnText']);
function getItemDetail(customMarketID) {
  getDataAPI({
    params: {
      CustomMarketID: `${customMarketID}SZ_`,
    },
  })
    .then((res) => {
      emit('showPopupDetail', {
        detailData: res.data.Response,
      });
    })
    .catch((error) => {
      console.log(error);
    });
}

const miniCartStore = useMiniCartStore();
const { getCartInfo } = miniCartStore;
function addToCart(idx) {
  if (selectedCustomMarketID.value[idx] === 'default') {
    alertDialog.alert('請選擇規格');
    return;
  }

  const utmData =
    utmKDetect(selectedCustomMarketID.value[idx]) !== null
      ? utmKDetect(selectedCustomMarketID.value[idx])
      : '';
  postShoppingCartAPI({
    CustomMarketID: selectedCustomMarketID.value[idx],
    Count: selectedCount.value[idx],
    UtmTag: utmData,
    ShareCode: shareCode() === null ? null : shareCode(),
  })
    .then((res) => {
      if (res.data.Code === 200) {
        messageBox.cart('加入購物車');
        getCartInfo();

        const selectSpecs = props.addonData[idx].Specs.find(
          (item) => item.CustomMarketID === selectedCustomMarketID.value[idx],
        );

        fireAddToCart({
          item_id: selectedCustomMarketID.value[idx].split('SZ')[0],
          item_name: props.addonData[idx].Name,
          index: 0,
          affiliation: selectSpecs.ColorName,
          item_brand: selectSpecs.Size,
          item_list_name: '產品內頁',
          price: props.addonData[idx].SellPrice,
          quantity: selectedCount.value[idx],
          item_variant: '',
        });
      } else if (res.data.Code === 205) {
        alertDialog.alert('商品已售完', () => {
          window.location.reload();
        });
      }
    })
    .catch((error) => {
      console.log(error);
    });
}

function onMinus(index) {
  if (selectedCount.value[index] <= 1) return;
  selectedCount.value[index] -= 1;
}

function onPlus(index) {
  if (selectedCustomMarketID.value[index] === 'default') {
    alertDialog.alert('請選擇商品規格');
    return;
  }
  const itemStock = stockArray.value[index].find(
    (item) => item.customMarketID === selectedCustomMarketID.value[index],
  );

  if (selectedCount.value[index] >= itemStock.stock) return;
  if (selectedCount.value[index] >= 10) return;

  selectedCount.value[index] += 1;
  // this.$set(this.selectedCount, index, this.selectedCount[index] + 1);
}

function openSelector() {
  selectIsOpen.value = !selectIsOpen.value;
}

function checkAddonInCart() {
  emit('showAddonWarnText');
}
</script>
